/* jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import Content from 'app/components/common/Content.jsx';
import SubaccountHeader from 'app/components/Headers/SubaccountHeader.jsx';
import Toggle from 'app/components/common/ToggleControl.jsx';
import FormUtilityMixin from 'app/mixins/FormUtilityMixin.js';
import ErrorModule from 'app/mixins/ErrorModule.jsx';
import FieldError from 'app/components/common/FieldError.jsx';
import FilterMixin from 'app/mixins/FilterMixin.js';
import UserMessages from 'app/modules/UserMessageModule.js';
import SubscriptionModule from 'app/mixins/SubscriptionModule.js';
import classnames from 'classnames';
import * as _ from 'underscore';
import { FormattedDate } from 'react-intl';
import DeleteModal from 'app/components/common/DeleteModal.jsx';
import UncontrolledTextInput from 'app/components/common/UncontrolledTextInput.jsx';
import EmptyState from 'app/components/common/EmptyState.jsx';
import { compose } from 'redux';
import SubaccountProvider from 'app/redux/Providers/SubaccountProvider.jsx';
import Utilities from 'app/utility/Utilities.js';
import FeatureUpgradeComponent from 'app/components/Subscription/FeatureUpgradeComponent.jsx';
import { Breakpoint } from 'react-socks';
import ComponentWithContext from 'app/components/Routers/ComponentWithContext.jsx';
import makeToast from 'app/modules/makeToast.js';

/**
 * Whether the team can be listed: by the account owner, on a plan with seats, where -1 is
 * unlimited. On a fresh load the plan's seats arrive after the page mounts, so this is asked again
 * as they come in.
 */
function canListTeam(props) {
    let seats = parseInt(props.user.perms.perms.extraSeats, 10);

    return props.app.userLoaded && props.user.detail.isAgent === false && !isNaN(seats) && seats !== 0;

}

class Users extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            detailId: 0,
            // Pending invitations are selected by address: they have no Publisher row,
            // so their id is 0 — which detailId already spends on "nothing selected".
            pendingEmail: '',
            subaccount: {},
            newSubaccount: {},
            errors: null,
            updateMessages: [],
            lastUpdate: ''
        };

        this._getSubaccounts = this._getSubaccounts.bind(this);
        this.createNewSubaccount = this.createNewSubaccount.bind(this);
        this.cancel = this.cancel.bind(this);
        this.detailHandler = this.detailHandler.bind(this);
        this.pendingDetailHandler = this.pendingDetailHandler.bind(this);
        this.resendInvite = this.resendInvite.bind(this);
        this.revokeInvite = this.revokeInvite.bind(this);
        this.updateSubaccount = this.updateSubaccount.bind(this);
        this.updateSubaccountEmail = this.updateSubaccountEmail.bind(this);
        this.setSubaccountActiveState = this.setSubaccountActiveState.bind(this);
        this.saveSubaccount = this.saveSubaccount.bind(this);
        this.deleteSubaccount = this.deleteSubaccount.bind(this);
        this.searchUsers = this.searchUsers.bind(this);
        this.onAddUserClick = this.onAddUserClick.bind(this);

    }


    _getSubaccounts() {

        if (!_.isEmpty(this.props.subaccounts.filter)) {
            return this.props.subaccounts.filteredList;
        }

        return  this.props.subaccounts.list;

    }

    static getDerivedStateFromProps(props, state) {
        let nextState = {},
            errors = null;

        errors = props.getApiErrors(['SUBACCOUNT_LIST', 'SUBACCOUNT_UPDATE', 'SUBACCOUNT_CREATE', 'SUBACCOUNT_DELETE']);
        if (state.errors === null && errors.length) {
            nextState.errors = errors[0];
        } else if (state.errors !== null && errors.length === 0) {
            nextState.errors = null;
        }

        return (Object.keys(nextState).length) ? nextState : null;

    }

    componentDidMount() {
        let id = parseInt(this.props.router.params.subaccountId, 10),
            constants = this.props.app.config.constants;

        // clean up errors from client if present
        this.props.deleteApiError(['SUBACCOUNT_LIST', 'SUBACCOUNT_UPDATE', 'SUBACCOUNT_CREATE', 'SUBACCOUNT_DELETE', 'SUBACCOUNT_DETACH', 'SUBACCOUNT_INVITE_RESEND', 'SUBACCOUNT_INVITE_REVOKE']);

        if (canListTeam(this.props)) {
            this.props.getSubaccounts({
                accountType: constants.SUB_AGENT
            });
        }

        if (id) {
            this.setState({
                detailId: id
            });
        }

        this.props.clearFilter('subaccounts');

    }

    componentDidUpdate(prevProps, prevState) {

        if (!canListTeam(prevProps) && canListTeam(this.props)) {
            this.props.getSubaccounts({
                accountType: this.props.app.config.constants.SUB_AGENT
            });
        }

        // show the interface for creating new users
        if (prevProps.app.loading.SUBACCOUNT_LIST === true && this.props.app.loading.SUBACCOUNT_LIST === false) {
            if (this.props.subaccounts.list.length === 0 && this.props.router.location.pathname.match('new') === null) {
                this.props.router.navigate('/people/team/new');
            }
            if (this.state.detailId !== 0 && _.isEmpty(this.state.subaccount)) {
                for (let i = 0; i < this.props.subaccounts.list.length; ++i) {
                    if (parseInt(this.state.detailId, 10) === parseInt(this.props.subaccounts.list[i].id)) {
                        this.setState({
                            subaccount: _.clone(this.props.subaccounts.list[i])
                        });
                        break;
                    }
                }
            }
        }

        if (prevState.errors === null && this.state.errors !== null) {
            if (this.state.errors.errors !== undefined && this.state.errors.errors.perms !== undefined) {
                this.props.showUpgradeModal(this.props.closeAllModals, null, this.state.errors, 'team_error');
            }
        }

        if ((prevProps.app.loading.SUBACCOUNT_UPDATE === true && this.props.app.loading.SUBACCOUNT_UPDATE === false) ||
            (prevProps.app.loading.SUBACCOUNT_DELETE === true && this.props.app.loading.SUBACCOUNT_DELETE === false) ||
            (prevProps.app.loading.SUBACCOUNT_CREATE === true && this.props.app.loading.SUBACCOUNT_CREATE === false)) {
            /*
             * An invitation was issued — return to the list view. Keyed on the create
             * request itself rather than on the list growing, because re-inviting an
             * address that already has an outstanding invitation replaces that row
             * instead of adding one.
             */
            if (prevProps.app.loading.SUBACCOUNT_CREATE === true && this.props.app.loading.SUBACCOUNT_CREATE === false &&
                this.props.getApiErrors(['SUBACCOUNT_CREATE']).length === 0 &&
                this.props.router.location.pathname.match('new') !== null) {
                this.props.router.navigate('/people/team');
                // refresh the user to update the default invitedBy (if necessary)
                this.props.getUser();
                this.props.getUserPerms();
                this.setState({
                    newSubaccount: {}
                });
            } else {
                // Only claim the mail went out when the update actually succeeded —
                // both of these are fire-and-forget sends with no other feedback.
                let updateFailed = this.props.getApiErrors(['SUBACCOUNT_UPDATE']).length > 0;
                if (this.state.lastUpdate === 'resetPassword') {
                    makeToast(updateFailed ? "We couldn't send that password reset email." : "Password reset email sent", updateFailed ? "error" : "success");
                } else if (this.state.lastUpdate === 'resendInvite') {
                    makeToast(updateFailed ? "We couldn't resend that invitation." : "Invitation email resent", updateFailed ? "error" : "success");
                }
                this.setState({
                    lastUpdate: ''
                });
            }

            // check if a user got updated and only rollup the detail if
            // an email has been updated
            if (this.state.detailId !== 0 && this.state.errors === null && this.state.lastUpdate === '') {
                this.setState({
                    detailId: 0,
                    subaccount: {},
                    lastUpdate: ''
                }, () => {
                    this.props.router.navigate('/people/team');
                });
            }
        }

        /*
         * Invite actions report through a toast, not the detail panel, so they're keyed
         * on their own request finishing and on whether it actually succeeded — the
         * older invite toast fired on the request completing either way.
         */
        if (prevProps.app.loading.SUBACCOUNT_INVITE_RESEND === true && this.props.app.loading.SUBACCOUNT_INVITE_RESEND === false) {
            let resendErrors = this.props.getApiErrors(['SUBACCOUNT_INVITE_RESEND']);
            if (resendErrors.length === 0) {
                makeToast("Invitation email resent", "success");
            } else {
                // the send-limit refusal names how long to wait, so prefer the server's
                // own wording over the generic copy when there is one
                let refusal = (resendErrors[0].errors && resendErrors[0].errors.validation) ? resendErrors[0].errors.validation.email : '';
                makeToast(refusal || "We couldn't resend that invitation.", "error");
            }
        }

        if (prevProps.app.loading.SUBACCOUNT_INVITE_REVOKE === true && this.props.app.loading.SUBACCOUNT_INVITE_REVOKE === false) {
            if (this.props.getApiErrors(['SUBACCOUNT_INVITE_REVOKE']).length === 0) {
                makeToast("Invitation withdrawn", "success");
                // the row is gone from the list; drop the selection with it
                this.setState({
                    pendingEmail: ''
                });
            } else {
                makeToast("We couldn't withdraw that invitation.", "error");
            }
        }

        if (this.props.router.location.pathname.match('new') && this.state.newSubaccount.invitedBy === undefined) {
            this.setState({
                newSubaccount: {
                    invitedBy: this.props.user.detail.name
                }
            });
        }

    }

    createNewSubaccount(e) {
        let constants = this.props.app.config.constants;

        e.preventDefault();

        this.props.deleteApiError(['SUBACCOUNT_CREATE', 'SUBACCOUNT_UPDATE']);
        this.state.newSubaccount.accountType = constants.SUB_AGENT;
        this.props.createSubaccount(this.state.newSubaccount);

    }

    cancel() {

        this.props.deleteApiError(['SUBACCOUNT_UPDATE', 'SUBACCOUNT_DELETE']);
        this.setState({
            detailId: 0,
            pendingEmail: '',
            subaccount: {},
            newSubaccount: {}
        }, () => {
            this.props.router.navigate('/people/team');
        });

    }

    detailHandler(e) {
        let id = parseInt(e.currentTarget.dataset.id, 10),
            subaccount = {};

        // a refusal was about the member it was shown with
        this.props.deleteApiError(['SUBACCOUNT_UPDATE', 'SUBACCOUNT_DELETE']);

        if (id === this.state.detailId) {
            this.setState({
                detailId: 0,
                pendingEmail: ''
            });
            this.props.router.navigate('/people/team');
        } else {
            for (let i = 0; i < this.props.subaccounts.list.length; ++i) {
                if (parseInt(id, 10) === parseInt(this.props.subaccounts.list[i].id)) {
                    subaccount = _.clone(this.props.subaccounts.list[i]);
                    break;
                }
            }
            this.setState({
                detailId: id,
                pendingEmail: '',
                subaccount
            });
            this.props.router.navigate('/people/team/detail/' + id);
        }

    }

    /*
     * Selection handler for pending invitations. There is no detail route to navigate
     * to (the account doesn't exist yet), so the expansion is local state only —
     * detailId stays 0 and never identifies a pending row.
     */
    pendingDetailHandler(e) {
        let email = e.currentTarget.dataset.email,
            wasDetail = this.state.detailId !== 0;

        this.setState({
            detailId: 0,
            pendingEmail: (email === this.state.pendingEmail) ? '' : email,
            subaccount: {}
        });

        if (wasDetail) {
            this.props.router.navigate('/people/team');
        }

    }

    updateSubaccountEmail(e) {
        let nextSubaccount = _.clone(this.state.subaccount);

        nextSubaccount.email = e.target.value;

        this.setState({
            subaccount: nextSubaccount
        });

    }

    updateSubaccount(e) {
        let subaccount = this.props.subaccounts.list.find((sub) => { return sub.id === this.state.detailId; }),
            field = e.currentTarget.dataset.field;

        e.preventDefault();

        if (subaccount) {
            let nextSubaccount = _.clone(subaccount);
            nextSubaccount[field] = 1;

            this.props.deleteApiError(['SUBACCOUNT_UPDATE']);
            this.props.updateSubaccount(nextSubaccount);

            this.setState({
                lastUpdate: field
            });
        }

    }

    setSubaccountActiveState(e) {
        let state = e.currentTarget.dataset.togglestate === "true" ? 0 : 1,
            // the member as stored rather than the draft, so a pause saves nothing else being edited
            subaccount = this.props.subaccounts.list.find((sub) => { return sub.id === this.state.detailId; });

        if (subaccount) {
            this.props.updateSubaccount(Object.assign({}, subaccount, {active: state}));
        }

    }

    saveSubaccount(e) {

        let i = 0,
            subaccount = null;

        e.preventDefault();

        //SubaccountActions.clearMessages('subaccount', this.state.detaiId);
        if (!_.isEmpty(this.state.subaccount)) {
            this.setState({
                errors: null
            }, () => {
                this.props.deleteApiError(['SUBACCOUNT_UPDATE']);
                this.props.updateSubaccount(this.state.subaccount);
            });

        }

    }

    deleteSubaccount(e) {

        let cancelDelete = () => {
                this.props.closeAllModals();
            },
            confirmDelete = (e) => {
                e.preventDefault();
                if (this.state.detailId) {
                    //SubaccountActions.clearMessages('subaccount', this.state.detaiId);
                    this.props.deleteSubaccount(this.state.detailId);
                }
                this.props.closeAllModals();
            },
            modal = (
                <DeleteModal
                    modalCopy={"Are you sure you want to remove this Team Member?"}
                    cancelAction={cancelDelete}
                    confirmAction={confirmDelete} />
            );

        e.preventDefault();

        this.props.addModal(modal);

    }

    resendInvite(e) {
        let email = e.currentTarget.dataset.email;

        e.preventDefault();

        this.props.deleteApiError(['SUBACCOUNT_INVITE_RESEND']);
        this.props.resendInvite(email);

    }

    revokeInvite(e) {
        let email = e.currentTarget.dataset.email,
            cancelRevoke = () => {
                this.props.closeAllModals();
            },
            confirmRevoke = (event) => {
                event.preventDefault();
                this.props.deleteApiError(['SUBACCOUNT_INVITE_REVOKE']);
                this.props.revokeInvite(email);
                this.props.closeAllModals();
            },
            modal = (
                <DeleteModal
                    modalCopy={"Are you sure you want to withdraw this invitation? The link we emailed will stop working."}
                    cancelAction={cancelRevoke}
                    confirmAction={confirmRevoke} />
            );

        e.preventDefault();

        this.props.addModal(modal);

    }

    searchUsers(e, value) {

        if (value === '') {
            this.props.clearFilter('subaccounts');
        } else {
            this.props.filterStore('subaccounts', 'email', value);
        }

    }

    onAddUserClick(e) {
        e.preventDefault();

        this.props.deleteApiError(['SUBACCOUNT_CREATE', 'SUBACCOUNT_UPDATE']);

        if (this.state.detailId || this.state.pendingEmail) {
            this.setState({
                detailId: 0,
                pendingEmail: '',
                newSubaccount: {
                    invitedBy: this.props.user.detail.name
                }
            });
        }

    }

    render() {
        let header = null,
            main = null,
            newUserItem = null,
            statusBlock = null,
            detailErrors = null,
            newEmail = (this.state.newSubaccount && this.state.newSubaccount.email) ? this.state.newSubaccount.email : '',
            newInvite = (this.state.newSubaccount && this.state.newSubaccount.invitedBy) ? this.state.newSubaccount.invitedBy : '';

        if (this.props.user.detail.isAgent) {
            let acctMessage = <EmptyState message={"Sorry, only the account owner can access this page."}/>;
            return (
                <Content header={header} main={acctMessage}/>
            );
        }

        if (parseInt(this.props.user.perms.perms.extraSeats, 10) === 0) {
            main = (
                <FeatureUpgradeComponent
                    {...this.props}
                    context="users"/>
            );
            return (
                <Content header={header} main={main}/>
            );
        }

        header = (
            <SubaccountHeader
                {...this.props}
                searchHandler={this.searchUsers}
                recordPath={this.props.recordPath}
                user={this.props.user}
                newSubaccountCallback={this.onAddUserClick} />
        );

        if (this.state.errors) {
            statusBlock = (
                <div className="detail-status error">
                    <ErrorModule
                        app={this.props.app}
                        keys={['SUBACCOUNT_CREATE', 'SUBACCOUNT_UPDATE']}
                        render={(state) => {
                            return (
                                <FieldError
                                    field="email"
                                    errorRE="Publisher already exists."
                                    errorReplacement="That email is already used by another dlvr.it account. Please send your invite to a different email address."
                                    errors={state.errors}/>
                            );
                        }}/>
                    <ErrorModule
                        app={this.props.app}
                        keys={['SUBACCOUNT_CREATE', 'SUBACCOUNT_UPDATE']}
                        render={(state) => {
                            return (
                                <FieldError field="invitedBy" errors={state.errors}/>
                            );
                        }}/>
                </div>
            );
        }

        // a member's detail says why a save, pause or removal was refused
        if (this.props.getApiErrors(['SUBACCOUNT_UPDATE', 'SUBACCOUNT_DELETE']).length) {
            detailErrors = (
                <div className="detail-status error">
                    {['email', 'active', 'id'].map((field) => (
                        <ErrorModule
                            key={field}
                            app={this.props.app}
                            keys={['SUBACCOUNT_UPDATE', 'SUBACCOUNT_DELETE']}
                            render={(state) => {
                                return (
                                    <FieldError field={field} errors={state.errors}/>
                                );
                            }}/>
                    ))}
                </div>
            );
        }

        if (this.props.router.location.pathname.match('new')) {
            newUserItem = (
                <li key="new" className="dlvr-list-item simple detail">
                    <div className="list-item-display">
                        <div className="item-type-icon">
                            <i className="fa fa-user new-subaccount"></i>
                        </div>
                        <div className="item-name common">
                            {(newEmail) ? newEmail : 'New Team Member'}
                        </div>
                    </div>
                    <div className="new-shortener-detail">
                        {statusBlock}
                        <div className="column-container">
                            <form>
                                <div className="editor-panel short">
                                    <fieldset>
                                        <legend>Team Member Email <span className="required">*</span></legend>
                                        <UncontrolledTextInput className="text-input large" placeholder="Invite new team member at email address" defaultValue={newEmail} onChange={FormUtilityMixin.requestChange.bind(this)} data-field="newSubaccount.email" />
                                    </fieldset>
                                    <fieldset>
                                        <legend>Invited By <span className="required">*</span></legend>
                                        <UncontrolledTextInput className="text-input large" defaultValue={newInvite} onChange={FormUtilityMixin.requestChange.bind(this)} data-field="newSubaccount.invitedBy"/>
                                    </fieldset>
                                </div>
                            </form>
                            <div className="explainer-wrapper">
                                <div className="explainer">
                                    <div className="headline">Invite Team Member</div>
                                    <div className="copy">We'll send an invitation to link them to this dlvr.it account.</div>
                                </div>
                            </div>
                        </div>
                        <div className="form-buttons standalone">
                            <div className="cancel-button" onClick={this.cancel} role="button" aria-label="cancel">cancel</div>
                            <button className="btn-primary active fixed" onClick={this.createNewSubaccount}>
                                Invite
                            </button>
                        </div>
                    </div>
                </li>
            );
        }

        main = (
            <ul className="dlvr-list-component">
                {newUserItem}
                {this._getSubaccounts().map(function(subaccount, i) {
                    // An outstanding invitation has no account behind it yet: no id, no
                    // login, no active state. Everything below branches on that rather
                    // than on id/lastLogin/active, each of which reads as a live value.
                    let pending = subaccount.pending === true,
                        inactive = parseInt(subaccount.active, 10) === 2 ? true : false,
                        isDetail = pending
                            ? (subaccount.email === this.state.pendingEmail)
                            : (subaccount.id === this.state.detailId),
                        hasSelection = (this.state.detailId !== 0 || this.state.pendingEmail !== ''),
                        itemClass = classnames(
            				'dlvr-list-item',
            				{
                                'inactive': inactive,
                                'pending': pending,
            					'detail': isDetail,
            					'blur': (hasSelection && !isDetail)
            				}
                        ),
                        itemNameClass = classnames(
                            'item-name', 'common',
                            {
                                'inactive': inactive
                            }
                        ),
                        statusClass = classnames(
                            "route-status", "common",
                            {
                                'active': (!pending && inactive === false),
                                'inactive': (!pending && inactive),
                                'pending': pending
                            }
                        ),
                        statusIcon = null,
                        itemDetail = null,
                        statusBlock = null,
                        lastLogin = new Date(subaccount.lastLogin),
                        inviteExpires = (pending && parseInt(subaccount.expiration, 10)) ? new Date(parseInt(subaccount.expiration, 10) * 1000) : null,
                        userActive = (parseInt(subaccount.active, 10) === 2) ? false : true,
                        subaccountIconClass = classnames(
                            'fa', 'fa-user', 'subaccount',
                            {
                                'inactive': inactive,
                                'pending': pending
                            }
                        ),
                        subaccountEmail = (this.state.subaccount && this.state.subaccount.email) ? this.state.subaccount.email : '';

                    if (pending) {
                        statusIcon = (
                            <div className="route-status-icon">
                                <i className="fa fa-clock-o"/>
                            </div>
                        );
                    } else if (parseInt(subaccount.active, 10) !== 2) {
                        statusIcon = (
                            <div className="route-status-icon">
                                <i className="fa fa-check-circle"/>
                            </div>
                        );
                    }  else {
                        statusIcon = (
                            <div className="route-status-icon">
                                <i className="fa fa-minus-circle"/>
                            </div>
                        );
                    }

                    if (pending) {
                        statusBlock = (
                            <div className="detail-status inactive">
                                {(inviteExpires) ? (
                                    <span>Invitation not accepted yet — expires <FormattedDate
                                                                                    value={inviteExpires}
                                                                                    day="numeric"
                                                                                    month="long"
                                                                                    year="numeric"/></span>
                                ) : (
                                    <span>Invitation not accepted yet.</span>
                                )}
                            </div>
                        );
                    } else if (!_.isNaN(lastLogin.getTime()) && parseInt(subaccount.active, 10)) {
                        statusBlock = (
                            <div className="detail-status info">
                                Last Login: <FormattedDate
                                                value={lastLogin}
                                                day="numeric"
                                                month="long"
                                                year="numeric"/>
                            </div>
                        );
                    } else {
                        if (_.isNaN(lastLogin.getTime()) && parseInt(subaccount.active, 10)) {
                            statusBlock = (
                                <div className="detail-status inactive">
                                    Team Member has not logged in.
                                </div>
                            );
                        }
                    }

                    // if (this.state.updateMessages && this.state.updateMessages.length) {
                    //     statusBlock = (
                    //         <div className="detail-status info">
                    //             {this.state.updateMessages.map(function(message, i) {
                    //                 return (<div key={i}>{this.getUserMessage(message)}</div>);
                    //             }, this)}
                    //         </div>
                    //     )
                    // }

                    if (isDetail && pending) {
                        // Nothing here can address a Publisher row, so the panel offers
                        // only the two things that act on the invitation itself.
                        itemDetail = (
                            <div>
                                <Breakpoint desktop only>
                                    <div className="detail-controls">
                                        <div className="detail-buttons">
                                            <div className="detail-button" onClick={this.revokeInvite} data-email={subaccount.email} role="button" aria-label="withdraw invitation">
                                                <div className="button-icon">
                                                    <i className="fa fa-remove"></i>
                                                </div>
                                                <div className="button-label">
                                                    Revoke
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Breakpoint>
                                <Breakpoint mobile only>
                                    <div className="detail-controls mobile">
                                        <div className="detail-control-row">
                                            <div className="detail-button" onClick={this.revokeInvite} data-email={subaccount.email} role="button" aria-label="withdraw invitation">
                                                <div className="button-icon">
                                                    <i className="fa fa-remove"></i>
                                                </div>
                                                <div className="button-label">
                                                    Revoke
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Breakpoint>
                                {statusBlock}
                                <div className="editor-panel short">
                                    <fieldset>
                                        <legend>Invited Email</legend>
                                        <span>{subaccount.email}</span>
                                        <div style={{margin: '40px 0'}}>
                                            <button className="btn-secondary fixed" onClick={this.resendInvite} data-email={subaccount.email}>
                                                Resend Invitation
                                            </button>
                                        </div>
                                    </fieldset>
                                </div>
                            </div>
                        )
                    } else if (isDetail) {
                        if (detailErrors) {
                            statusBlock = detailErrors;
                        }
                        itemDetail = (
                            <div>
                                <Breakpoint desktop only>
                                    <div className="detail-controls">
                                        <div className="detail-toggle">
                                            <Toggle
                                                active={userActive}
                                                noLabel={true}
                                                ariaLabel={`${subaccount.email} active`}
                                                callback={this.setSubaccountActiveState} />
                                        </div>
                                        <div className="detail-buttons">
                                            <div className="detail-button" onClick={this.deleteSubaccount} data-id={subaccount.id} role="button" aria-label="remove user">
                                                <div className="button-icon">
                                                    <i className="fa fa-remove"></i>
                                                </div>
                                                <div className="button-label">
                                                    Delete
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Breakpoint>
                                <Breakpoint mobile only>
                                    <div className="detail-controls mobile">
                                        <div className="detail-control-row">
                                            <div className="detail-toggle">
                                                <Toggle
                                                    active={userActive}
                                                    ariaLabel={`${subaccount.email} active`}
                                                    callback={this.setSubaccountActiveState} />
                                            </div>
                                            <div className="detail-button" onClick={this.deleteSubaccount} data-id={subaccount.id} role="button" aria-label="remove user">
                                                <div className="button-icon">
                                                    <i className="fa fa-remove"></i>
                                                </div>
                                                <div className="button-label">
                                                    Delete
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Breakpoint>
                                {statusBlock}
                                <div className="editor-panel short">
                                    <fieldset>
                                        <legend>Team Member's Email <span className="required">*</span></legend>
                                        <UncontrolledTextInput className="text-input medium" defaultValue={subaccountEmail} onChange={this.updateSubaccountEmail} data-field="email"/>
                                        <div style={{margin: '40px 0'}}>
                                            <button className="btn-secondary fixed" onClick={this.updateSubaccount} data-field="resendInvite">
                                                Resend Invite Email
                                            </button>
                                        </div>
                                        <div style={{margin: '40px 0'}}>
                                            <button className="btn-secondary fixed" onClick={this.updateSubaccount} data-field="resetPassword">
                                                Send Password Reset Email
                                            </button>
                                        </div>
                                    </fieldset>
                                    <fieldset>
                                        <div className="form-buttons">
                                            <div className="cancel-button" onClick={this.cancel} role="button" aria-label="cancel">cancel</div>
                                            <button className="btn-primary active fixed" onClick={this.saveSubaccount}>
                                                Save
                                            </button>
                                        </div>
                                    </fieldset>
                                </div>
                            </div>
                        )
                    }

                    return (
                        <li key={i} className={itemClass}>
                            <div className="list-item-display"
                                onClick={(pending) ? this.pendingDetailHandler : this.detailHandler}
                                data-id={subaccount.id}
                                data-email={subaccount.email}
                                role="switch"
                                aria-label="show details">
                                <div className="item-type-icon">
                                    <i className={subaccountIconClass}></i>
                                </div>
                                <div className={itemNameClass}>
                                    {subaccount.email}
                                </div>
                                <div className={statusClass}>
                                    {(pending) ? (<span className="route-status-label">Invitation pending</span>) : null}
                                    {statusIcon}
                                </div>
                            </div>
                            {itemDetail}
                        </li>
                    );
                }, this)}
            </ul>
        );

        return (
            <Content header={header} main={main} />
        );
    }

};

Users.displayName = "Users";

Users.propTypes = {
    userPerms: PropTypes.object,
    routeViewType: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    recordPath: PropTypes.func,
    trialEligible: PropTypes.bool
};

Users.defaultProps = {
    userPerms: {},
    routeViewType: 0,
    recordPath: null,
    trialEligible: false
};

const enhance = compose(
    ComponentWithContext,
    SubaccountProvider
);

export default enhance(Users);
